import "@capone/checkout/build/assets/style.css";
import React, { useState, useEffect } from "react";
import {
  Box,
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core";
import { IntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { Route, Router, Switch } from "react-router-dom";
import { AgentBanner } from "halifax";
import {
  ExperiencesClientAssetProps,
  ExperiencesModuleProps,
  CallState,
} from "redmond";
import { UserSourceProvider } from "@capone/common";
import { useMedalliaFeedback } from "b2b-base/src/components/MedalliaFeedback";
import useWindowEventListener from "./hooks/useWindowEventListener";
import { eventsToListen } from "./utils/events";
import * as frenchTranslations from "./lang/fr.json";
import * as englishTranslations from "./lang/en.json";
import ExperimentsProvider from "./context/experiments";
import { fetchUserInfo } from "./api/v0/user/fetchUserInfo";
import { store } from "./store";
import AxiosInterceptors from "./components/AxiosInterceptors";
import {
  PATH_AVAILABILITY,
  PATH_BOOK,
  PATH_BOOK_CONFIRMATION,
  PATH_HOME,
  PATH_HOME_SEARCH,
  PATH_SHOP,
} from "./utils/paths";
import { RewardsBanner } from "./modules/rewards/components";
import { ExperiencesSearch } from "./modules/search";
import { ExperiencesAvailability } from "./modules/availability";
import { ExperiencesShop } from "./modules/shop";
import { ActivityBook } from "./modules/book";

function loadLocaleData(locale: string): any {
  switch (locale) {
    case "fr":
      return frenchTranslations;
    default:
      return englishTranslations;
  }
}
const generateClassName = createGenerateClassName({
  productionPrefix: "ptExperiencesModule",
  seed: "ptExperiencesModule",
});

interface ExperiencesClientContextType extends ExperiencesClientAssetProps {
  isAgentPortal: boolean;
  isAutoApprovalEnabled: boolean;
  showCoreHomepage?: boolean;
}

export const ClientContext = React.createContext<
  Partial<ExperiencesClientContextType>
>({});

const App = (props: ExperiencesModuleProps) => {
  const {
    experiments,
    baseHistory,
    isAgentPortal,
    clientAssets,
    core,
    showCoreHomepage,
  } = props;

  const [activeTheme, setActiveTheme] = useState(props.theme);
  const [locale, setLocale] = useState(props.language);
  const [messages, setMessages] = useState(loadLocaleData(locale).default);
  const [sessionInfo, setSessionInfo] = useState(clientAssets.sessionInfo);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userInfoResponse = await fetchUserInfo();
        setSessionInfo(userInfoResponse);
      } catch (error) {
        console.error(error);
      }
    };
    fetchUser();
  }, []);

  const handleThemeChanged = (e: CustomEvent) => {
    setActiveTheme(e.detail);
    console.log("THEME RECEIVED:", e.detail.palette.type);
  };
  const handleLocaleChanged = (e: CustomEvent) => {
    setLocale(e.detail);
    const messages = loadLocaleData(e.detail);
    setMessages(messages.default);
  };
  useWindowEventListener(eventsToListen.HOST_THEME_CHANGED, handleThemeChanged);
  useWindowEventListener(
    eventsToListen.HOST_LOCALE_CHANGED,
    handleLocaleChanged
  );

  const { firstName, lastName } = sessionInfo?.userInfo || {
    firstName: "",
    lastName: "",
  };

  return (
    <Provider store={store}>
      <ExperimentsProvider
        initState={{
          experiments: experiments?.experiments || [],
          trackingProperties: experiments?.trackingProperties,
          callState: CallState.NotCalled,
        }}
      >
        <UserSourceProvider>
          <Router history={baseHistory}>
            <AxiosInterceptors isAgentPortal={isAgentPortal} />
            <ClientContext.Provider
              value={{ ...clientAssets, sessionInfo, showCoreHomepage }}
            >
              <div className="App">
                <StylesProvider generateClassName={generateClassName}>
                  <ThemeProvider theme={activeTheme}>
                    {messages != null ? (
                      <IntlProvider
                        locale={locale}
                        defaultLocale="en"
                        messages={messages}
                      >
                        <Switch>
                          {isAgentPortal ? (
                            <Route path="*">
                              <AgentBanner
                                firstName={firstName}
                                lastName={lastName}
                              />
                            </Route>
                          ) : (
                            <Route path="*">
                              <RewardsBanner />
                            </Route>
                          )}
                        </Switch>
                        <Body core={core} />
                      </IntlProvider>
                    ) : (
                      <></>
                    )}
                  </ThemeProvider>
                </StylesProvider>
              </div>
            </ClientContext.Provider>
          </Router>
        </UserSourceProvider>
      </ExperimentsProvider>
    </Provider>
  );
};

export const Body = ({
  core,
}: {
  core?: ExperiencesModuleProps["core"];
}) => {
  useMedalliaFeedback();

  return (
    <Box className="main-section">
      <Route path={[PATH_HOME, PATH_HOME_SEARCH]} exact>
        <ExperiencesSearch />
      </Route>
      <Route path={PATH_AVAILABILITY}>
        <ExperiencesAvailability />
      </Route>
      <Route path={PATH_SHOP}>
        <ExperiencesShop />
      </Route>
      <Route path={[PATH_BOOK, PATH_BOOK_CONFIRMATION]}>
        <ActivityBook core={core} />
      </Route>
    </Box>
  );
};
export default App;
